import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { Menu, Button, Typography, Divider } from 'antd';
import type { MenuProps } from 'antd';
import {
  LogoutOutlined,
  SearchOutlined,
  FileTextOutlined,
  SendOutlined,
  GlobalOutlined,
  DatabaseOutlined,
  FileOutlined,
  PlusCircleOutlined,
  BarChartOutlined,
  CloudUploadOutlined,
  EyeOutlined,
  NumberOutlined,
  WarningOutlined,
  CloseOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  menuRegions,
  getMenuOpenKeysForPath,
  type MenuGroupConfig,
  type MenuLeafConfig,
} from '../../../utils/menuConfig';

const { Text } = Typography;

interface DashboardSidebarProps {
  theme: 'light' | 'dark';
  isDark: boolean;
  isDrawer: boolean;
  collapsed: boolean;
  selectedKey: string;
  onCloseDrawer?: () => void;
  onLogout: () => void;
}

const leafIcons: Record<string, React.ReactNode> = {
  '/dashboard/status': <DashboardOutlined />,
  '/dashboard/api-consulta': <BarChartOutlined />,
  '/buscar-fonte': <SearchOutlined />,
  '/push/buscar-id': <NumberOutlined />,
  '/api-callback': <FileOutlined />,
  '/abrangencia': <GlobalOutlined />,
  '/status/ocorrencias': <WarningOutlined />,
  '/integrations/solucionare': <CloudUploadOutlined />,
  '/consumos': <BarChartOutlined />,
};

const groupIcons: Record<string, React.ReactNode> = {
  'api-consulta': <EyeOutlined />,
  autorequest: <FileTextOutlined />,
  capturaweb: <PlusCircleOutlined />,
  push: <SendOutlined />,
  dt: <DatabaseOutlined />,
};

const buildLeafItem = (item: MenuLeafConfig): NonNullable<MenuProps['items']>[number] => ({
  key: item.path,
  icon: leafIcons[item.path],
  label: item.label,
});

const buildGroupItem = (item: MenuGroupConfig): NonNullable<MenuProps['items']>[number] => ({
  key: item.key,
  icon: groupIcons[item.key],
  label: item.label,
  children: item.children.map((child) => (
    'children' in child ? buildGroupItem(child) : buildLeafItem(child)
  )),
});

const buildMenuSection = (key: string, label: string, isDark: boolean, topMargin = 16) => ({
  key,
  disabled: true,
  label: (
    <Divider
      plain
      orientation="left"
      style={{
        margin: '4px 0',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Divider>
  ),
  style: { cursor: 'default', padding: '0 4px', height: 'auto', marginTop: topMargin },
});

const buildMenuItems = (isDark: boolean): MenuProps['items'] => {
  const items: NonNullable<MenuProps['items']> = [];

  menuRegions.forEach((regionConfig, regionIndex) => {
    items.push(buildMenuSection(
      `sep-${regionConfig.region}`,
      regionConfig.region,
      isDark,
      regionIndex === 0 ? 0 : 16,
    ));

    regionConfig.items.forEach((item) => {
      items.push('children' in item ? buildGroupItem(item) : buildLeafItem(item));
    });
  });

  return items;
};

const logoutMenuItems: MenuProps['items'] = [
  {
    key: '/logout',
    icon: <LogoutOutlined />,
    label: 'Sair',
    danger: true,
  },
];

/**
 * Sidebar do layout — componente memoizado para não remontar ao trocar de página.
 */
const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  theme,
  isDark,
  isDrawer,
  collapsed,
  selectedKey,
  onCloseDrawer,
  onLogout,
}) => {
  const navigate = useNavigate();
  const pathOpenKeys = useMemo(
    () => getMenuOpenKeysForPath(selectedKey),
    [selectedKey],
  );
  const [openKeys, setOpenKeys] = useState<string[]>(pathOpenKeys);

  // Garante submenus da rota atual abertos sem bloquear expansão manual
  useEffect(() => {
    setOpenKeys((prev) => {
      const merged = new Set([...prev, ...pathOpenKeys]);
      const next = Array.from(merged);
      const isSame = next.length === prev.length && next.every((key) => prev.includes(key));
      return isSame ? prev : next;
    });
  }, [pathOpenKeys]);

  const handleOpenChange = useCallback((keys: string[]) => {
    setOpenKeys(keys);
  }, []);

  const menuItems = useMemo(() => buildMenuItems(isDark), [isDark]);

  const handleMenuClick: MenuProps['onClick'] = useCallback((event: { key: string }) => {
    if (event.key === '/logout') {
      onLogout();
      return;
    }

    if (!event.key.startsWith('/')) {
      return;
    }

    if (event.key !== selectedKey) {
      navigate(event.key);
    }

    if (isDrawer) {
      onCloseDrawer?.();
    }
  }, [isDrawer, navigate, onCloseDrawer, onLogout, selectedKey]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: isDark ? '#141414' : '#ffffff',
      }}
    >
      <div
        style={{
          padding: !isDrawer && collapsed ? '16px 8px' : '20px',
          textAlign: !isDrawer && collapsed ? 'center' : 'left',
          borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: !isDrawer && collapsed ? 'center' : 'space-between',
        }}
      >
        <div>
          {!isDrawer && collapsed ? (
            <Text strong style={{ color: '#4DAB4B', fontSize: '18px' }}>
              SR
            </Text>
          ) : (
            <div>
              <Text strong style={{ color: '#4DAB4B', fontSize: '20px' }}>
                SupReq
              </Text>
              <br />
              <Text
                type="secondary"
                style={{
                  fontSize: '12px',
                  color: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.45)',
                }}
              >
                O melhor amigo do suporte
              </Text>
            </div>
          )}
        </div>
        {isDrawer && (
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCloseDrawer}
            style={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.45)' }}
          />
        )}
      </div>

      <Menu
        className="app-sidebar-menu"
        theme={theme}
        mode="inline"
        inlineCollapsed={!isDrawer && collapsed}
        selectedKeys={[selectedKey]}
        openKeys={!isDrawer && collapsed ? [] : openKeys}
        onOpenChange={handleOpenChange}
        onClick={handleMenuClick}
        items={menuItems}
        style={{
          flex: 1,
          borderRight: 0,
          backgroundColor: 'transparent',
          paddingTop: 12,
        }}
      />

      <div style={{ borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)' }}>
        <Menu
          className="app-sidebar-menu"
          theme={theme}
          mode="inline"
          inlineCollapsed={!isDrawer && collapsed}
          selectedKeys={[]}
          onClick={handleMenuClick}
          items={logoutMenuItems}
          style={{ borderRight: 0, backgroundColor: 'transparent' }}
        />
      </div>
    </div>
  );
};

export default memo(DashboardSidebar);
